import type { FragmentType } from 'src/gql';
import { useState } from 'react';
import { Separator } from '@savvy-kit/components/separator';
import { isNil } from 'es-toolkit';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { graphql, unmaskFragment } from 'src/gql';

import { ClientTaxDetails } from './client-tax-details';
import { TaxEnrollment } from './tax-enrollment';
import { TaxEnrollmentDialog } from './tax-enrollment-dialog';
import { CURRENT_TAX_YEAR, getLastTranscriptStatus, isTaxAddendumPending, isTaxAddendumRequired } from './tax-utils';

export const HouseholdTaxesFragment = graphql(/* GraphQL */ `
  fragment HouseholdTaxes on Household {
    id
    name
    ...TaxEnrollmentForm
    primaryClient {
      user {
        fullName
      }
      taxEnrollments {
        id
        taxYear
        enrollmentType
        price
        federalReturnStatus
        stateReturnStatus
        updatedAt
      }
      taxTranscriptRequests {
        id
        updatedAt
        status
      }
      onboardingIaaCreatedAt
      clientAgreements {
        id
        agreementType
        signatureId
      }
    }
    coClient {
      user {
        fullName
      }
      taxEnrollments {
        id
        taxYear
        enrollmentType
        price
        federalReturnStatus
        stateReturnStatus
        updatedAt
      }
      taxTranscriptRequests {
        id
        updatedAt
        status
      }
      onboardingIaaCreatedAt
      clientAgreements {
        id
        agreementType
        signatureId
      }
    }
  }
`);

type HouseholdTaxEnrollmentProps = {
  readonly householdFrag: FragmentType<typeof HouseholdTaxesFragment> | undefined;
  readonly loading: boolean;
};

export default function HouseholdTaxEnrollment({ householdFrag, loading }: HouseholdTaxEnrollmentProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { showTaxAddendum, flagEnableTaxEnrollment } = useFlags();
  const household = unmaskFragment(HouseholdTaxesFragment, householdFrag);

  if (!household) {
    return null;
  }

  const primaryTaxEnrollments = household.primaryClient.taxEnrollments;
  const currentPrimaryTaxEnrollment = primaryTaxEnrollments?.find(
    (taxEnrollment) => taxEnrollment.taxYear === CURRENT_TAX_YEAR,
  );

  if (!currentPrimaryTaxEnrollment) {
    return flagEnableTaxEnrollment ? (
      <>
        <TaxEnrollment householdFrag={household} loading={loading} />
        <TaxEnrollmentDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          householdFrag={household}
          loading={loading}
        />
      </>
    ) : null;
  }

  const coClientTaxEnrollments = household.coClient?.taxEnrollments;
  const coClientTaxEnrollment = coClientTaxEnrollments?.find(
    (taxEnrollment) => taxEnrollment.taxYear === CURRENT_TAX_YEAR,
  );

  const primaryTranscriptStatus = getLastTranscriptStatus(household.primaryClient.taxTranscriptRequests);
  const coClientTranscriptStatus = getLastTranscriptStatus(household.coClient?.taxTranscriptRequests);

  const primaryTaxAddendumRequired = isTaxAddendumRequired(!!showTaxAddendum, household.primaryClient);
  const primaryTaxAddendumPending = isTaxAddendumPending(!!showTaxAddendum, household.primaryClient);
  const coClientTaxAddendumRequired = isTaxAddendumRequired(!!showTaxAddendum, household.coClient ?? undefined);
  const coClientTaxAddendumPending = isTaxAddendumPending(!!showTaxAddendum, household.coClient ?? undefined);

  const jointFiling = currentPrimaryTaxEnrollment.enrollmentType === 'savvy_joint';
  /** If the filing is joint, we need to show the household name and addendum status for both */
  const jointNameOverride = jointFiling ? household.name : household.primaryClient.user.fullName;
  const jointTaxAddendumRequired = jointFiling
    ? primaryTaxAddendumRequired || coClientTaxAddendumRequired
    : primaryTaxAddendumRequired;
  const jointTaxAddendumPending = jointFiling
    ? primaryTaxAddendumPending || coClientTaxAddendumPending
    : primaryTaxAddendumPending;
  return (
    <div className="flex flex-col gap-8">
      <h1 className="text-2xl font-semibold">Current tax filing</h1>
      <div className="flex gap-10">
        <ClientTaxDetails
          name={jointNameOverride}
          taxEnrollment={currentPrimaryTaxEnrollment}
          taxTranscriptStatus={primaryTranscriptStatus}
          taxTranscriptRequests={household.primaryClient.taxTranscriptRequests}
          onUpdate={() => setIsDialogOpen(true)}
          taxAddendumRequired={jointTaxAddendumRequired}
          taxAddendumPending={jointTaxAddendumPending}
        />
        {!isNil(coClientTaxEnrollment) && !isNil(household.coClient) && !jointFiling && (
          <>
            <div className="flex">
              <Separator orientation="vertical" className="bg-border-light" />
            </div>
            <ClientTaxDetails
              name={household.coClient.user.fullName}
              taxEnrollment={coClientTaxEnrollment}
              taxTranscriptStatus={coClientTranscriptStatus}
              taxTranscriptRequests={household.coClient.taxTranscriptRequests}
              onUpdate={() => setIsDialogOpen(true)}
              taxAddendumRequired={coClientTaxAddendumRequired}
              taxAddendumPending={coClientTaxAddendumPending}
            />
          </>
        )}
      </div>
      <Separator className="bg-border-light" />
      <TaxEnrollmentDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        householdFrag={household}
        loading={loading}
      />
    </div>
  );
}
